import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardBody, CardHeader } from "reactstrap";

import { H4 } from "../../../../AbstractElements";
import { useAppDispatch, useAppSelector } from "../../../../ReduxToolkit/Hooks";
import {
  createSurvey,
  getSurveyDetail,
  updateSurvey,
} from "../../../../ReduxToolkit/Reducers/SurveySlice";
import {
  SurveyTitleError,
  SurveyDescriptionError,
  SurveyBasicDetails,
} from "../../../../utils/Constant";
import { fileUrlKeys, extractFileName } from "../../../../utils/helper/helper";

import SurveyBuilder from "./components/SurveyBuilder";
import FirstStep, { getApiPayload } from "./FirstStep";

type SurveyForm = {
  title: string;
  description: string;
  isForCourse: boolean;
  courseId: string;
  courseDuration: string;
  isForNewEmployee: boolean;
  newEmpDays: string;
  newEmpDuration: string;
  markMultiple: boolean;
  resubmissionLimit: string;
  showWronglyAnswered: boolean;
  surveyFor: string;
  onboarding_survey_offset: string;
};
type SurveyErrors = Partial<Record<keyof SurveyForm, string>>;

const CreateSurvey = () => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<SurveyForm | any>({
    title: "",
    description: "",
    isForCourse: false,
    courseId: "",
    courseDuration: "",
    isForNewEmployee: false,
    newEmpDays: "",
    newEmpDuration: "",
    markMultiple: false,
    resubmissionLimit: "",
    showWronglyAnswered: false,
    surveyFor: "",
    onboarding_survey_offset: "",
  });
  const [errors, setErrors] = useState<SurveyErrors>({});
  const [surveyId, setSurveyId] = useState<number | null>(null);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const { surveyDetails } = useAppSelector(state => state.survey);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for survey-id and type=duplicate in query params and fetch details if present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("survey-id");
    const type = params.get("type");
    if (id) {
      const numId = Number(id);
      setSurveyId(numId);
      dispatch(getSurveyDetail(numId));
    }
    setIsDuplicate(type === "duplicate");
  }, [location.search, dispatch]);

  // Prefill form when surveyDetails is loaded
  useEffect(() => {
    if (surveyId && surveyDetails && surveyDetails.id === surveyId) {
      setForm({
        title: isDuplicate ? "" : surveyDetails.title || "",
        description: surveyDetails.description || "",
        surveyFor:
          surveyDetails.course_id != null && surveyDetails.course_id !== ""
            ? "course"
            : surveyDetails.onboarding_survey_offset != null &&
                surveyDetails.onboarding_survey_offset !== ""
              ? "employee_onboard"
              : "",
        courseId:
          surveyDetails.course_id != null && surveyDetails.course_id !== ""
            ? String(surveyDetails.course_id)
            : "",
        courseDuration:
          surveyDetails.duration != null && surveyDetails.duration !== ""
            ? String(surveyDetails.duration)
            : "",
        onboarding_survey_offset:
          surveyDetails.onboarding_survey_offset != null &&
          surveyDetails.onboarding_survey_offset !== ""
            ? String(surveyDetails.onboarding_survey_offset)
            : "",
        markMultiple: !!surveyDetails.is_multiple_submissions,
        resubmissionLimit:
          surveyDetails.resubmission_limit != null &&
          surveyDetails.resubmission_limit !== ""
            ? String(surveyDetails.resubmission_limit)
            : "",
        showWronglyAnswered: !!surveyDetails.show_wrongly_answered,
      });
    }
  }, [surveyId, surveyDetails, isDuplicate]);

  // Helper to deeply remove all id fields from sections/questions/options
  const stripIds = (sections = []) => {
    return sections.map(section => {
      const { id, ...sectionRest } = section;
      return {
        ...sectionRest,
        questions: (section.questions || []).map(q => {
          const { id: qid, ...qRest } = q;
          // Format media_url for duplicate as well
          let formattedMediaUrl = "";
          if (qRest.media_url && typeof qRest.media_url === "string") {
            formattedMediaUrl =
              fileUrlKeys.survey_files + "/" + extractFileName(qRest.media_url);
          }
          return {
            ...qRest,
            media_url: formattedMediaUrl,
            options: (q.options || []).map(opt => {
              if (typeof opt === "string") return opt;
              const { id: oid, ...optRest } = opt;
              return { ...optRest };
            }),
          };
        }),
      };
    });
  };

  const formatMediaUrlInSections = (sections = []) => {
    return sections.map(section => ({
      ...section,
      questions: (section.questions || []).map(q => ({
        ...q,
        media_url:
          q.media_url && typeof q.media_url === "string"
            ? fileUrlKeys.survey_files + "/" + extractFileName(q.media_url)
            : "",
      })),
    }));
  };

  const handleNext = async () => {
    const newErrors: SurveyErrors = {};
    if (!form.title || form.title.length > 100)
      newErrors.title = SurveyTitleError;
    if (!form.description || form.description.length > 256)
      newErrors.description = SurveyDescriptionError;
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      let sectionsToSend;
      let branchingRulesToSend;
      if (surveyId && surveyDetails && surveyDetails.sections) {
        // Format media_url for all questions before sending
        sectionsToSend = formatMediaUrlInSections(surveyDetails.sections);
        branchingRulesToSend = surveyDetails.branching_rules || [];
      } else {
        sectionsToSend = [
          {
            title: "Section 1",
            description: "",
            section_order: 1,
            questions: [],
          },
        ];
        branchingRulesToSend = [];
      }
      const extraFields = getApiPayload(form);
      let payload;
      if (isDuplicate) {
        // Remove all ids for duplicate
        payload = {
          title: form.title,
          description: form.description,
          status: "draft",
          sections: stripIds(sectionsToSend),
          branching_rules: (branchingRulesToSend || []).map(rule => ({
            ...rule,
            id: undefined,
            parent_question_id: undefined,
            child_question_id:
              rule.child_question_id === undefined ||
              rule.child_question_id === ""
                ? null
                : rule.child_question_id,
            option_id: undefined,
          })),
          ...extraFields,
        };
      } else {
        payload = {
          ...(surveyId ? { id: surveyId } : {}),
          title: form.title,
          description: form.description,
          status: "draft",
          sections: sectionsToSend,
          branching_rules: branchingRulesToSend,
          ...extraFields,
        };
      }
      let resultAction;
      if (isDuplicate) {
        resultAction = await dispatch(createSurvey(payload));
        // After first create, treat as normal edit for next steps
        if (resultAction.payload?.id) {
          setSurveyId(resultAction.payload.id);
          setIsDuplicate(false); // Only first time
          await dispatch(getSurveyDetail(resultAction.payload.id));
        }
        setStep(2);
      } else if (surveyId) {
        resultAction = await dispatch(updateSurvey(payload));
        const newId = surveyId || resultAction.payload?.id;
        setSurveyId(newId);
        await dispatch(getSurveyDetail(newId));
        setStep(2);
      } else {
        resultAction = await dispatch(createSurvey(payload));
        const newId = resultAction.payload?.id;
        setSurveyId(newId);
        await dispatch(getSurveyDetail(newId));
        setStep(2);
      }
    }
  };

  const handleBack = async payload => {
    // Format media_url for all questions before sending
    if (payload.sections) {
      payload.sections = formatMediaUrlInSections(payload.sections);
    }
    let newId = payload.id;
    const extraFields = getApiPayload(form);
    const fullPayload = { ...payload, ...extraFields };
    if (payload.id) {
      await dispatch(updateSurvey(fullPayload));
    } else {
      const result = await dispatch(createSurvey(fullPayload));
      newId = result.payload?.id;
    }
    // Always fetch the latest survey details
    if (newId) {
      await dispatch(getSurveyDetail(newId));
    }
    setStep(1);
  };

  const handleSubmit = async payload => {
    // Format media_url for all questions before sending
    if (payload.sections) {
      payload.sections = formatMediaUrlInSections(payload.sections);
    }
    const extraFields = getApiPayload(form);
    const fullPayload = { ...payload, ...extraFields };
    let result;
    if (payload.id) {
      result = await dispatch(updateSurvey(fullPayload));
    } else {
      result = await dispatch(createSurvey(fullPayload));
    }
    // If API call is successful, navigate to Manage Surveys
    if (!result.error) {
      navigate("/manage-surveys"); // Adjust the route if needed
    }
    // ...any other logic for Next/Submit
  };

  return (
    <div className="page-body page-body-margin pb-5">
      <Card className="p-3">
        <CardHeader>
          <H4>{SurveyBasicDetails}</H4>
        </CardHeader>
        <CardBody>
          {step === 1 && (
            <FirstStep
              form={form}
              setForm={setForm}
              errors={errors}
              setErrors={setErrors}
              onNext={handleNext}
            />
          )}
          {step === 2 && (
            <SurveyBuilder
              onBack={handleBack}
              onSubmit={handleSubmit}
              onSaveDraft={data => handleSubmit({ ...data, status: "draft" })}
              surveySections={surveyDetails?.sections || []}
              surveyQuestions={
                surveyDetails?.sections
                  ? surveyDetails.sections.flatMap(s => s.questions || [])
                  : []
              }
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default CreateSurvey;
